import React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FallingLines, ThreeDots } from 'react-loader-spinner';
import './css/Verify.css';
import { verifyEmail, setUserPassword } from '../../Utils/RestUtils';
import { changeLoginModalVisibility } from '../../DataStore/actions/modalWindowActions';
import {
  PASSWORD_LABEL,
  SAVE,
  ENTER,
  REGISTER_SUCCESS,
  SYSTEM_FAULT,
  VERIFY_LOADING,
  VERIFY_FORM_TITLE,
  VERIFY_FORM_TEXT,
  VERIFY_PASSWORD_HINT,
  VERIFY_SHOW_PASSWORD,
  VERIFY_HIDE_PASSWORD,
  VERIFY_SUCCESS_TEXT,
  VERIFY_ERROR_TITLE,
  VERIFY_ERROR_TEXT,
  TO_MAIN_PAGE,
} from '../../Utils/Constants/GlobalConstants';
import { ReactComponent as Success } from '../../images/success-r.svg';

const LOADING_STAGE = 'loading';
const FORM_STAGE = 'form';
const SUCCESS_STAGE = 'success';
const ERROR_STAGE = 'error';

const renderLoading = () => (
  <div className="v_card v_card_center">
    <div className="mb_12">
      <ThreeDots
        visible
        height="40"
        width="60"
        color="#039"
        radius="5"
        ariaLabel="three-dots-loading"
      />
    </div>
    <div className="gr_text_16">{VERIFY_LOADING}</div>
  </div>
);

const mapStateToProps = (state) => ({ ...state });

class Verify extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      stage: LOADING_STAGE,
      passwordValue: '',
      email: '',
      code: '',
      isPasswordValid: false,
      isPasswordVisible: false,
      isSubmitSpinnerVisible: false,
      errorMessage: '',
    };
  }

  componentDidMount() {
    const { location } = this.props;

    const queryParameters = new URLSearchParams(location.search);
    const email = queryParameters.get('email');
    const code = queryParameters.get('code');
    const passwordPresent = queryParameters.get('pass') === 'true';

    if (email && code) {
      this.setState({ email, code });
      this.verify({ email, code }, passwordPresent);
    } else {
      this.setState({ stage: ERROR_STAGE });
    }
  }

  componentDidUpdate(prevProps) {
    const { isUserLoggedIn, navigate } = this.props;

    // user logged in via the login modal opened from this page
    if (isUserLoggedIn && !prevProps.isUserLoggedIn) {
      navigate('/');
    }
  }

  handlePasswordChange = (e) => {
    const passwordValue = e.target.value;
    this.setState({
      passwordValue,
      isPasswordValid: passwordValue.length >= 8,
      errorMessage: '',
    });
  };

  handlePasswordVisibilityClick = () => {
    const { isPasswordVisible } = this.state;
    this.setState({ isPasswordVisible: !isPasswordVisible });
  };

  handleSubmit = (e) => {
    e.preventDefault();

    const {
      passwordValue,
      email,
      code,
      isPasswordValid,
      isSubmitSpinnerVisible,
    } = this.state;

    if (!isPasswordValid || isSubmitSpinnerVisible) {
      return;
    }

    this.setState({ isSubmitSpinnerVisible: true, errorMessage: '' });

    // backend sets the password only with the code from the email link
    const payload = {
      password: passwordValue,
      email,
      code,
    };

    setUserPassword(payload)
      .then((response) => {
        if (response && response.data === 'success') {
          this.setState({ stage: SUCCESS_STAGE });
        } else {
          this.setState({ errorMessage: SYSTEM_FAULT });
        }
      })
      .catch(() => {
        this.setState({ errorMessage: SYSTEM_FAULT });
      })
      .finally(() => {
        this.setState({ isSubmitSpinnerVisible: false });
      });
  };

  handleLoginClick = () => {
    const { changeLoginModalVisibility: changeLoginModalVisibilityAction } =
      this.props;
    changeLoginModalVisibilityAction(true);
  };

  getHintClass = () => {
    const { isPasswordValid } = this.state;
    return `v_hint ${isPasswordValid ? 'greenr_text_14' : 'gr_text_14'}`;
  };

  getSubmitClass = () => {
    const { isPasswordValid } = this.state;
    return `v_button wm_text_16 ${
      isPasswordValid ? 'blue_button' : 'v_button_dis'
    }`;
  };

  verify(payload, passwordPresent) {
    verifyEmail(payload)
      .then((response) => {
        if (response && response.data === 'success') {
          this.setState({
            stage: passwordPresent ? SUCCESS_STAGE : FORM_STAGE,
          });
        } else {
          this.setState({ stage: ERROR_STAGE });
        }
      })
      .catch(() => {
        this.setState({ stage: ERROR_STAGE });
      });
  }

  renderForm = () => {
    const {
      email,
      passwordValue,
      isPasswordValid,
      isPasswordVisible,
      isSubmitSpinnerVisible,
      errorMessage,
    } = this.state;

    return (
      <form className="v_card" onSubmit={this.handleSubmit}>
        <div className="bm_text_24 mb_12">{VERIFY_FORM_TITLE}</div>
        <div className="gr_text_16 mb_8">{VERIFY_FORM_TEXT}</div>
        <div className="bm_text_16 v_email mb_30">{email}</div>

        {/* lets password managers bind the new password to the email */}
        <input
          type="email"
          value={email}
          autoComplete="username"
          readOnly
          hidden
        />

        <label className="gr_text_14 mb_8" htmlFor="v_password">
          {PASSWORD_LABEL}
        </label>
        <div className="v_input_w">
          <input
            id="v_password"
            value={passwordValue}
            className="v_input"
            onChange={this.handlePasswordChange}
            type={isPasswordVisible ? 'text' : 'password'}
            autoComplete="new-password"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <button
            type="button"
            className="v_input_toggle bluem_text_14"
            onClick={this.handlePasswordVisibilityClick}
          >
            {isPasswordVisible ? VERIFY_HIDE_PASSWORD : VERIFY_SHOW_PASSWORD}
          </button>
        </div>
        <div className={this.getHintClass()}>{VERIFY_PASSWORD_HINT}</div>

        <button
          type="submit"
          className={this.getSubmitClass()}
          disabled={!isPasswordValid || isSubmitSpinnerVisible}
        >
          <div className="v_button_content">
            {SAVE}
            <div className="v_button_loader">
              <FallingLines
                color="#ffffff"
                width="30"
                visible={isSubmitSpinnerVisible}
              />
            </div>
          </div>
        </button>
        <div className="v_error rr_text_14">{errorMessage}</div>
      </form>
    );
  };

  renderSuccess = () => (
    <div className="v_card v_card_center">
      <Success className="mb_20" />
      <div className="bm_text_24 mb_12">{REGISTER_SUCCESS}</div>
      <div className="gr_text_16 v_text mb_30">{VERIFY_SUCCESS_TEXT}</div>
      <button
        type="button"
        className="v_button wm_text_16 blue_button mb_12"
        onClick={this.handleLoginClick}
      >
        {ENTER}
      </button>
      <Link className="v_button v_button_frame dbm_text_16" to="/">
        {TO_MAIN_PAGE}
      </Link>
    </div>
  );

  renderError = () => (
    <div className="v_card v_card_center">
      <div className="bm_text_24 mb_12">{VERIFY_ERROR_TITLE}</div>
      <div className="gr_text_16 v_text mb_30">{VERIFY_ERROR_TEXT}</div>
      <button
        type="button"
        className="v_button wm_text_16 blue_button mb_12"
        onClick={this.handleLoginClick}
      >
        {ENTER}
      </button>
      <Link className="v_button v_button_frame dbm_text_16" to="/">
        {TO_MAIN_PAGE}
      </Link>
    </div>
  );

  renderStage = () => {
    const { stage } = this.state;

    switch (stage) {
      case FORM_STAGE:
        return this.renderForm();
      case SUCCESS_STAGE:
        return this.renderSuccess();
      case ERROR_STAGE:
        return this.renderError();
      default:
        return renderLoading();
    }
  };

  render() {
    return (
      <div className="content">
        <div className="v_intro" />
        <div className="container v_container">{this.renderStage()}</div>
      </div>
    );
  }
}

Verify.propTypes = {
  location: PropTypes.instanceOf(Object).isRequired,
  navigate: PropTypes.func.isRequired,
  isUserLoggedIn: PropTypes.bool.isRequired,
  changeLoginModalVisibility: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, { changeLoginModalVisibility })(Verify);
